<script>
    import Fa from 'svelte-fa';
    import { faCartShopping, faArrowLeft } from '@fortawesome/pro-regular-svg-icons';

    import { page } from '$app/stores';
    import { subscribe } from '$lib/utils/pubsub';
    import { cartUpdate } from '$lib/utils/shopify/cart/events';
    import { notification } from '$lib/utils/notifications';
    import { formatMoney } from '$lib/utils/functions/money.js';
    import { checkoutHandler } from '$lib/utils/functions/checkoutHandler';

    import Button from '$lib/components/Button.svelte';
    import CartItem from '$lib/components/CartItem.svelte';
    import CartAccordian from '$lib/components/CartAccordian.svelte';

    export let setDrawerCallback;

    let cartData = {};
    let cartUpdating = false;

    $: cartItems = cartData?.cart?.lines?.edges || [];
    $: discountCodes = cartData?.cart?.discountCodes || [];

    $: estimatedShippingCost = cartData?.cart?.deliveryGroups?.edges?.[0]?.node?.selectedDeliveryOption?.estimatedCost?.amount || 0;
    $: cartTotalToPay = cartData?.cart?.cost?.totalAmount?.amount - estimatedShippingCost;
    $: cartTotalBeforeDiscounts = cartItems?.reduce((total, cartItem) => total + Number(cartItem?.node?.cost?.subtotalAmount?.amount), 0) ?? 0;
    $: compareAtSavingsTotal = cartItems.reduce((total, cartItem) => {
        const compareAtPrice = cartItem?.node?.merchandise?.compareAtPrice?.amount * cartItem?.node?.quantity;
        const productPrice = cartItem?.node?.merchandise?.price?.amount * cartItem?.node?.quantity;
        const totalSavings = compareAtPrice && compareAtPrice > productPrice ? total + (compareAtPrice - productPrice) : total;
        return totalSavings;
    }, 0);

    $: cartCost = {
        currency: cartData?.cart?.cost?.totalAmount?.currencyCode,
        shipping: Number(estimatedShippingCost).toFixed(2),
        checkoutTotal: Number(cartTotalToPay).toFixed(2),
        subtotal: Number(cartTotalBeforeDiscounts).toFixed(2),
        savings: Number(cartTotalBeforeDiscounts - cartTotalToPay).toFixed(2), // Discout code allocations (does not include compareAt savings)
        compareAtSavings: Number(compareAtSavingsTotal).toFixed(2)
    };

    // pubsub cartData
    subscribe(cartUpdate, (message) => {
        cartData = message;
    });

    // recieves true/false to open cart draw from pubSub message
    // triggers the callback function in Drawer.svelte
    $: if (cartData?.openDrawerState === true) {
        setDrawerCallback(true);
    }

    // cart error notifications
    $: if (cartData.userErrors) {
        cartData.userErrors.forEach((error) => {
            notification(error.message);
        });
    }

    // Add discount code to cart from searchParams
    $: if ($page.url.searchParams.has('discount') && cartData?.cart?.id) {
        const discountCodeToAdd = $page.url.searchParams.get('discount');
        const isDiscountCodeToAddInArray = discountCodes.some((discount) => discount.code === discountCodeToAdd);

        if (!isDiscountCodeToAddInArray) {
            let addCodeArray = discountCodes.map((discount) => discount?.code);
            addCodeArray.push(discountCodeToAdd);
            window.cart.updateCartDiscountCodes(addCodeArray);
            notification(`Code '${discountCodeToAdd}' was added to your cart`);
        }
    }
</script>

<div class="p-4">
    {#if cartData?.cart?.totalQuantity > 0}
        <div class="pb-4">
            <button
                class="pr-4"
                on:click={() => {
                    history.back();
                }}
                role="menuitem"
            >
                <Fa icon={faArrowLeft} class="inline-block" />
            </button>
            <div class="inline-block text-left text-xl font-semibold dark:text-purple-300">Cart</div>
        </div>

        <div class="no-scrollbar max-h-screen overflow-y-auto overflow-x-hidden pb-80">
            <div class="grid grid-cols-1 gap-y-2">
                {#each cartItems as cartItem (cartItem)}
                    <CartItem item={cartItem?.node} bind:cartUpdating />
                {/each}
            </div>
        </div>

        <div
            class="absolute bottom-0 left-0 flex w-full flex-col gap-4 rounded-tl-xl rounded-tr-xl bg-color-background/80 px-4 py-5 backdrop-blur-xl dark:bg-base-900/60"
        >
            <CartAccordian {cartCost} {discountCodes} bind:cartUpdating />

            <Button style="Featured" onclick={() => checkoutHandler(setDrawerCallback, cartData?.cart?.checkoutUrl)} disabled={cartUpdating} vibrate={10}>
                {#if cartUpdating}
                    Updating...
                {:else}
                    <div class="grid w-full grid-cols-2 items-center">
                        <div class="col-span-1 text-left">
                            <Fa icon={faCartShopping} class="mr-2 inline-block" /> <span class="inline-block">Checkout</span>
                        </div>
                        <div class="col-span-1 text-right">
                            <span>
                                {cartCost?.currency}
                                {formatMoney(cartCost?.checkoutTotal)}
                            </span>
                        </div>
                    </div>
                {/if}
            </Button>
        </div>
    {:else}
        <div class="mt-7 text-center">
            <div class="py-4 text-2xl">Cart</div>
            <p>Your Cart is Empty</p>
        </div>
    {/if}
</div>
